/**
 * ForcePasswordChange.js - Forced password change screen for admin-created accounts
 *
 * Shown when user.mustChangePassword is true. Blocks all navigation until
 * the user sets a new password.
 */

import React, { useState } from 'react';
import Logo from './Logo';
import * as api from '../services/api';

function ForcePasswordChange({ onPasswordChanged, onLogout }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from your temporary password');
      return;
    }

    setLoading(true);
    try {
      await api.changePassword({ currentPassword, newPassword });
      onPasswordChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '1rem'
    }}>
      <div style={{
        position: 'absolute',
        top: '1rem',
        right: '1rem'
      }}>
        <button
          onClick={onLogout}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: '1px solid rgba(255,255,255,0.3)',
            color: '#fff',
            padding: '0.5rem 1rem',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: '0.85rem'
          }}
        >
          Log Out
        </button>
      </div>

      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: '2.5rem 2rem',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <Logo size="medium" variant="gradient" />
        </div>

        <h2 style={{
          textAlign: 'center',
          margin: '0 0 0.5rem',
          color: '#333',
          fontSize: '1.3rem'
        }}>
          Change Your Password
        </h2>

        <p style={{
          textAlign: 'center',
          color: '#718096',
          fontSize: '0.9rem',
          margin: '0 0 1.5rem',
          lineHeight: 1.5
        }}>
          Your account was created by an administrator with a temporary password.
          Please set a new password to continue.
        </p>

        {error && (
          <div style={{
            background: '#fff5f5',
            border: '1px solid #fecaca',
            color: '#e53e3e',
            padding: '0.75rem 1rem',
            borderRadius: 8,
            fontSize: '0.85rem',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#4a5568',
              marginBottom: '0.25rem'
            }}>
              Temporary Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              placeholder="Enter the password given to you"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.95rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#4a5568',
              marginBottom: '0.25rem'
            }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              placeholder="At least 6 characters"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.95rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#4a5568',
              marginBottom: '0.25rem'
            }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="Re-enter your new password"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.95rem',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: loading || !currentPassword || !newPassword || !confirmPassword
                ? '#e2e8f0'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Changing Password...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ForcePasswordChange;
